import { useEffect, useRef } from "react";

const LERP = 0.09;

/** Subtle full-page glass wash — smooth follow, low contrast (desktop pointer only). */
export default function CursorSpotlight() {
  const spotRef = useRef(null);
  const target = useRef({ x: -9999, y: -9999 });
  const smooth = useRef({ x: -9999, y: -9999 });
  const visible = useRef(false);
  const raf = useRef(0);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const narrow = window.matchMedia("(max-width: 768px)").matches;
    if (reduced || coarse || narrow) return undefined;

    const el = spotRef.current;
    if (!el) return undefined;

    const tick = () => {
      const dx = target.current.x - smooth.current.x;
      const dy = target.current.y - smooth.current.y;
      smooth.current.x += dx * LERP;
      smooth.current.y += dy * LERP;

      el.style.transform = `translate3d(${smooth.current.x}px, ${smooth.current.y}px, 0)`;

      if (visible.current && (Math.abs(dx) > 0.4 || Math.abs(dy) > 0.4)) {
        raf.current = requestAnimationFrame(tick);
      } else {
        raf.current = 0;
      }
    };

    const queue = () => {
      if (!raf.current) raf.current = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      target.current = { x: e.clientX, y: e.clientY };
      if (!visible.current) {
        smooth.current = { ...target.current };
        visible.current = true;
        el.classList.add("is-visible");
      }
      queue();
    };

    const onLeave = () => {
      visible.current = false;
      el.classList.remove("is-visible");
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return <div ref={spotRef} className="cursor-spotlight" aria-hidden="true" />;
}
